import { EMPTY_COLLECTION_DTL_SEARCH_CRITERIA } from "domain/entity/Collection/CollectionDetailSearchCriteria";
import { CollectionConstant } from "presentation/constant/Collection/CollectionConstant";
import { useCollectionVM } from "presentation/hook/Collection/useCollectionVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useCollectionTracked } from "presentation/store/Collection/CollectionProvider";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import NumberInputComponent from "presentation/view/components/NumberInputComponent";
import { memo, useCallback, useMemo, useState } from "react";
import { FieldType, HPHButton, IconButton, IFieldValue, Loader } from "veronica-ui-component/dist/component/core";
import { SidebarActionBar, SidebarActionCross, Sidebarheader, SidebarTitle } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";


const CollectionDtlEditPanel = () => {
    const SCREEN_CONSTANT = CollectionConstant;
    const [collectionState] = useCollectionTracked();
    const collectionVM = useCollectionVM();
    const { currentDtlEditRow, isEdit, selectedRows, allFormState } = collectionState;
    const messageBarVM = useMessageBarVM();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const saveButtonClicked = useCallback(async () => {
        if (await collectionVM.onSaveClicked(currentDtlEditRow)) {
            return;
        }
        if (currentDtlEditRow.paymentMode === 'PO') {
            if (currentDtlEditRow.payOrderAmount && currentDtlEditRow.paymentAmount && currentDtlEditRow.payOrderAmount < currentDtlEditRow.paymentAmount) {
                messageBarVM.showError("Pay order amount must greater or equal to payment amount.");
                return;
            }
        }

        if (currentDtlEditRow.paymentAmount && currentDtlEditRow.paymentAmount <= 0) {
            messageBarVM.showError("Payment Amount should be greater than 0.");
            return;
        }

        setIsLoading(true);
        let criteria = { ...EMPTY_COLLECTION_DTL_SEARCH_CRITERIA };
        criteria = {
            ...criteria,
            selectRows: selectedRows,
            dtl: currentDtlEditRow,
        }
        collectionVM.onSaveCollectionDtl(criteria).then(data => {
            if (data && data.success) {
                // let criteria = { ...EMPTY_COLLECTION_DTL_SEARCH_CRITERIA };
                // criteria = {
                //     ...criteria,
                //     selectRows: selectedRows,
                //     allRows: collectionHdrList
                // }
                const hdrIds = selectedRows.map(ety => ety.id);
                collectionVM.searchDtl(hdrIds).then(data => {
                    // collectionVM.onSearchDtl(criteria).then(data => {
                    setIsLoading(false);
                    messageBarVM.showSuccess("Save Successfully.");
                });
                setIsLoading(false);
            } else {
                messageBarVM.showError(data.data ?? "Save Failed.");
                setIsLoading(false);
                return;
            }
        });
    }, [collectionVM, currentDtlEditRow, messageBarVM, selectedRows]);

    const closeButtonClicked = async () => {
        collectionVM.onCloseEidtPanel();
    }

    const onNumberFieldChange = useCallback((e: any, fieldName: string, fieldValue: any) => {
        collectionVM.onEditFieldChange(fieldName, fieldValue);
    }, [collectionVM]);


    const memoPaymentMode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={isEdit}
                isShowOptional={false}
                readOnlyValue={currentDtlEditRow?.paymentMode || ''}
                fieldValue={currentDtlEditRow?.paymentMode}
                fieldLabel={SCREEN_CONSTANT.PAYMENT_MODE}
                isSaveClicked={true}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'paymentMode'}
                maxLength={60}
                sort={false}
                isLimitOptions={true}
                options={collectionState.dynamicOptions.paymentModeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    collectionVM.onEditFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, isEdit, currentDtlEditRow?.paymentMode, SCREEN_CONSTANT.PAYMENT_MODE, collectionState.dynamicOptions.paymentModeDropdownOptions, collectionVM])

    const memoPayee = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={currentDtlEditRow?.payee ?? ''}
                fieldValue={currentDtlEditRow?.payee}
                fieldLabel={SCREEN_CONSTANT.PAYEE}
                isSaveClicked={true}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'payee'}
                maxLength={60}
                sort={false}
                isLimitOptions={true}
                options={collectionState.dynamicOptions.payeeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    collectionVM.onEditFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, currentDtlEditRow?.payee, SCREEN_CONSTANT.PAYEE, collectionState.dynamicOptions.payeeDropdownOptions, collectionVM])

    const memoBankCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={isEdit || currentDtlEditRow.paymentMode === 'CASH'}
                isShowOptional={false}
                readOnlyValue={currentDtlEditRow?.bankCode ?? ''}
                fieldValue={currentDtlEditRow?.bankCode}
                fieldLabel={SCREEN_CONSTANT.BANK_CODE}
                isSaveClicked={true}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'bankCode'}
                maxLength={60}
                sort={false}
                isLimitOptions={true}
                options={collectionState.dynamicOptions.bankCodeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    collectionVM.onEditFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, isEdit, currentDtlEditRow.paymentMode, currentDtlEditRow?.bankCode, SCREEN_CONSTANT.BANK_CODE, collectionState.dynamicOptions.bankCodeDropdownOptions, collectionVM])

    const memoPayOrderNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={isEdit || currentDtlEditRow.paymentMode === 'CASH'}
                isShowOptional={false}
                readOnlyValue={currentDtlEditRow?.payOrderNo ?? ''}
                fieldValue={currentDtlEditRow?.payOrderNo}
                fieldLabel={SCREEN_CONSTANT.PAY_ORDER_NO}
                isSaveClicked={true}
                fieldType={FieldType.TEXT}
                fieldKey={'payOrderNo'}
                maxLength={20}
                sort={false}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    collectionVM.onEditFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, isEdit, currentDtlEditRow.paymentMode, currentDtlEditRow?.payOrderNo, SCREEN_CONSTANT.PAY_ORDER_NO, collectionVM])

    const memoPayeeName = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={true}
                isShowOptional={false}
                readOnlyValue={currentDtlEditRow?.payeeName ?? ''}
                fieldValue={currentDtlEditRow?.payeeName}
                fieldLabel={SCREEN_CONSTANT.PAYEE_NAME}
                isSaveClicked={true}
                fieldType={FieldType.TEXT}
                fieldKey={'payeeName'}
                maxLength={60}
                sort={false}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    collectionVM.onEditFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, currentDtlEditRow?.payeeName, SCREEN_CONSTANT.PAYEE_NAME, collectionVM])
    const memoAgentNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentDtlEditRow?.agentNo ?? ''}
                fieldValue={currentDtlEditRow?.agentNo}
                fieldLabel={SCREEN_CONSTANT.AGENT_NO}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'agentNo'}
                maxLength={60}
                sort={false}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    collectionVM.onEditFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [currentDtlEditRow?.agentNo, SCREEN_CONSTANT.AGENT_NO, collectionVM])

    const memoAgentName = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentDtlEditRow?.agentName ?? ''}
                fieldValue={currentDtlEditRow?.agentName}
                fieldLabel={SCREEN_CONSTANT.AGENT_NAME}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'agentName'}
                maxLength={60}
                sort={false}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    collectionVM.onEditFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [currentDtlEditRow?.agentName, SCREEN_CONSTANT.AGENT_NAME, collectionVM])

    const memoRemark = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "470px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentDtlEditRow?.remark ?? ''}
                fieldValue={currentDtlEditRow?.remark}
                fieldLabel={SCREEN_CONSTANT.REMARK}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'remark'}
                maxLength={60}
                sort={false}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    collectionVM.onEditFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [currentDtlEditRow?.remark, SCREEN_CONSTANT.REMARK, collectionVM])

    const memoPoExcessAmount = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NumberInputComponent
                label={SCREEN_CONSTANT.PO_EXCESS_AMOUNT}
                maxLength={10}
                fieldName={"poExcessAmount"}
                value={currentDtlEditRow?.poExcessAmount ?? ''}
                errorMessage={''}
                disabled={true}
                decimalPlaces={2}
                onChange={(e: any, fieldKey: string, fieldValue: any) => onNumberFieldChange(e, fieldKey, fieldValue)}
            />
        </div>
        , [SCREEN_CONSTANT.PO_EXCESS_AMOUNT, currentDtlEditRow?.poExcessAmount, onNumberFieldChange])

    const memoPayOrderAmount = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NumberInputComponent
                label={SCREEN_CONSTANT.PAY_ORDER_AMOUNT}
                maxLength={13}
                fieldName={"payOrderAmount"}
                value={currentDtlEditRow?.payOrderAmount ?? ''}
                errorMessage={allFormState?.payOrderAmount ?? ''}
                disabled={isEdit || currentDtlEditRow.paymentMode === 'CASH'}
                decimalPlaces={2}
                onChange={(e: any, fieldKey: string, fieldValue: any) => onNumberFieldChange(e, fieldKey, fieldValue)}
            />
        </div>
        , [allFormState, isEdit, SCREEN_CONSTANT.PAY_ORDER_AMOUNT, currentDtlEditRow?.payOrderAmount, currentDtlEditRow.paymentMode, onNumberFieldChange])

    const memoPaymentAmount = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NumberInputComponent
                label={SCREEN_CONSTANT.PAYMENT_AMOUNT}
                maxLength={13}
                fieldName={"paymentAmount"}
                value={currentDtlEditRow?.paymentAmount ?? ''}
                errorMessage={allFormState?.paymentAmount ?? ''}
                disabled={true}
                decimalPlaces={2}
                onChange={(e: any, fieldKey: string, fieldValue: any) => onNumberFieldChange(e, fieldKey, fieldValue)}
            />
        </div>
        , [allFormState, SCREEN_CONSTANT.PAYMENT_AMOUNT, currentDtlEditRow?.paymentAmount, onNumberFieldChange])

    const memoBalance = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NumberInputComponent
                label={SCREEN_CONSTANT.BALANCE}
                maxLength={10}
                fieldName={"balance"}
                value={currentDtlEditRow?.balance ?? ''}
                errorMessage={''}
                disabled={true}
                decimalPlaces={2}
                onChange={(e: any, fieldKey: string, fieldValue: any) => onNumberFieldChange(e, fieldKey, fieldValue)}
            />
        </div>
        , [SCREEN_CONSTANT.BALANCE, currentDtlEditRow?.balance, onNumberFieldChange])


    return (
        <div className='side-form-content-wrapper'>
            <div className={'flex-row'}>
                <div className={'flex-row-item flex-row-item-full-width'}>
                    <Sidebarheader>
                        <SidebarTitle>
                            {/* {TransferOfChargeConstant.BULK_EDIT} */}
                        </SidebarTitle>
                        <SidebarActionCross>
                            <IconButton
                                fileName="Icon-cross" size="medium" toolTipText='Close' toolTipArrow={false} onClick={closeButtonClicked} />
                        </SidebarActionCross>
                    </Sidebarheader>
                </div>
            </div>
            {isLoading &&
                <Loader Indicator="Spinner" size="Medium" />}
            {!isLoading && <>
                <div className={'add-edit-form'} style={{ height: '75vh', maxHeight: '75vh', overflow: 'auto' }}>
                    <div className="flex-row-auto">
                        <div className={'flex-row-item'}>
                            <CriteriaItemContainer>
                                {memoPaymentMode}
                                {memoBankCode}
                            </CriteriaItemContainer>
                            <CriteriaItemContainer>
                                {memoPayOrderNo}
                                {memoAgentNo}
                                {memoAgentName}
                            </CriteriaItemContainer>
                            <CriteriaItemContainer>
                                {memoPoExcessAmount}
                                {memoPayOrderAmount}
                            </CriteriaItemContainer>
                            <CriteriaItemContainer>
                                {memoPaymentAmount}
                                {memoBalance}
                            </CriteriaItemContainer>
                            <CriteriaItemContainer>
                                {memoPayee}
                                {memoPayeeName}
                            </CriteriaItemContainer>
                            <CriteriaItemContainer>
                                {memoRemark}
                            </CriteriaItemContainer>
                        </div>
                    </div>
                </div>
                <SidebarActionBar>
                    <HPHButton label={'Close'} size={'Small'} theme={'Secondary'} onClick={closeButtonClicked} />
                    <HPHButton label={'Save'} size={'Small'} theme={'Primary'} onClick={saveButtonClicked} />
                </SidebarActionBar>
            </>}
        </div>
    );
}

export default memo(CollectionDtlEditPanel);